import "./styles.css";
import { fetchBoard, submitLead, submitScore, validateLeadClient } from "./api";
import { AudioBus } from "./audio";
import { BRAND, STORAGE } from "./config";
import { Game } from "./game/game";
import type { RunStats } from "./game/types";
import { shareRun } from "./share";

const canvas = must("#game", HTMLCanvasElement);
const audio = new AudioBus();
const ui = {
  start: must("#start", HTMLElement),
  over: must("#over", HTMLElement),
  board: must("#board", HTMLElement),
  hud: must("#hud", HTMLElement),
  hint: must("#tap-hint", HTMLElement),
  mute: must("#mute", HTMLButtonElement),
};

let lastStats: RunStats = {
  housesLit: 0,
  streak: 0,
  bestStreak: 0,
  score: 0,
  energy: 0,
  clipped: 0,
};
let lastScreen: "start" | "over" = "start";

const game = new Game(canvas, audio, {
  onHud: (stats, speed) => {
    lastStats = stats;
    must("#hud-houses", HTMLElement).textContent = String(stats.housesLit);
    must("#hud-score", HTMLElement).textContent = String(stats.score);
    must("#hud-streak", HTMLElement).textContent = stats.streak > 1 ? `Streak ${stats.streak}x` : "Streak 0";
    must("#hud-energy", HTMLElement).style.width = `${Math.max(0, Math.min(100, stats.energy))}%`;
    must("#tap-hint", HTMLElement).textContent = speed <= 0
      ? "Tap the street to start running"
      : "Tap to jump · hold for a bigger jump";
  },
  onGameOver: (stats) => {
    lastStats = stats;
    saveBest(stats);
    showOver(stats);
  },
});

bindBrand();
refreshBest();
const params = new URLSearchParams(location.search);
if (params.get("screen") === "over") {
  lastStats = { housesLit: 12, streak: 4, bestStreak: 4, score: 28, energy: 40, clipped: 1 };
  saveBest(lastStats);
  showOver(lastStats);
} else if (params.get("auto") === "1") {
  game.enableAuto();
  audio.unlock();
  beginRun();
} else {
  game.preview();
}
void renderBoard();

must("#play", HTMLButtonElement).addEventListener("click", () => {
  audio.unlock();
  beginRun();
});

must("#retry", HTMLButtonElement).addEventListener("click", () => {
  audio.unlock();
  beginRun();
});

must("#show-board", HTMLButtonElement).addEventListener("click", () => {
  lastScreen = "start";
  show("board");
  void renderBoard();
});
must("#over-board", HTMLButtonElement).addEventListener("click", () => {
  lastScreen = "over";
  show("board");
  void renderBoard();
});
must("#board-back", HTMLButtonElement).addEventListener("click", () => {
  show(lastScreen);
});

must("#share", HTMLButtonElement).addEventListener("click", async () => {
  const status = must("#share-status", HTMLElement);
  status.textContent = "Building your card…";
  try {
    const how = await shareRun(lastStats.housesLit, lastStats.score);
    status.textContent =
      how === "shared"
        ? "Sent. Tell a neighbor to beat it."
        : how === "copied"
          ? "Card downloaded and the link is on your clipboard."
          : "Card downloaded. Send it to someone who still climbs their own ladder.";
  } catch {
    status.textContent = "Could not share from this browser. Screenshot works too.";
  }
});

must("#lead-email-score", HTMLInputElement).addEventListener("change", (e) => {
  const on = (e.target as HTMLInputElement).checked;
  must("#email-wrap", HTMLElement).classList.toggle("hidden", !on);
});

must("#lead-form", HTMLFormElement).addEventListener("submit", async (e) => {
  e.preventDefault();
  const form = e.currentTarget as HTMLFormElement;
  const hp = (form.elements.namedItem("company_website") as HTMLInputElement | null)?.value;
  if (hp) return;
  const name = must("#lead-name", HTMLInputElement).value;
  const phone = must("#lead-phone", HTMLInputElement).value;
  const zip = must("#lead-zip", HTMLInputElement).value;
  const emailScore = must("#lead-email-score", HTMLInputElement).checked;
  const email = must("#lead-email", HTMLInputElement).value;
  const msg = must("#lead-msg", HTMLElement);
  const check = validateLeadClient({ name, phone, zip, emailScore, email });
  if (!check.ok) {
    msg.className = "form-msg bad";
    msg.textContent = check.message;
    return;
  }
  const btn = must("#lead-submit", HTMLButtonElement);
  btn.disabled = true;
  msg.className = "form-msg";
  msg.textContent = "Sending…";
  const result = await submitLead({
    name,
    phone,
    zip,
    email,
    emailScore,
    score: lastStats.score,
    housesLit: lastStats.housesLit,
  });
  btn.disabled = false;
  msg.className = result.ok && result.served ? "form-msg good" : "form-msg bad";
  msg.textContent = result.ok && result.served
    ? result.city
      ? `Got it. We'll call about ${result.city}.`
      : result.message
    : result.message;
});

must("#board-form", HTMLFormElement).addEventListener("submit", async (e) => {
  e.preventDefault();
  if (lastStats.score <= 0 && lastStats.housesLit <= 0) {
    must("#board-msg", HTMLElement).textContent = "Play a run first, then post it.";
    return;
  }
  const name = must("#board-name", HTMLInputElement).value;
  const board = await submitScore(name, lastStats.score, lastStats.housesLit);
  paintBoard(board);
  must("#board-msg", HTMLElement).textContent = "Posted for this week.";
});

ui.mute.addEventListener("click", () => {
  const muted = audio.toggleMute();
  ui.mute.textContent = muted ? "×" : "♪";
  ui.mute.setAttribute("aria-label", muted ? "Unmute sound" : "Mute sound");
});

function beginRun(): void {
  show("play");
  game.start();
}

function showOver(stats: RunStats): void {
  must("#over-houses", HTMLElement).textContent = String(stats.housesLit);
  must("#over-score", HTMLElement).textContent = String(stats.score);
  const best = readBest();
  must("#over-best", HTMLElement).textContent =
    `Best streak this run: ${stats.bestStreak}. Personal best: ${best.houses} houses / ${best.score} points.`;
  must("#share-status", HTMLElement).textContent = "";
  must("#lead-msg", HTMLElement).textContent = "";
  show("over");
}

function show(which: "start" | "over" | "board" | "play"): void {
  ui.start.classList.toggle("hidden", which !== "start");
  ui.over.classList.toggle("hidden", which !== "over");
  ui.board.classList.toggle("hidden", which !== "board");
  ui.hud.classList.toggle("hidden", which !== "play");
  ui.hint.classList.toggle("hidden", which !== "play");
  ui.mute.classList.toggle("hidden", which === "start");
}

function bindBrand(): void {
  must("#call-link", HTMLAnchorElement).href = `tel:${BRAND.phoneTel}`;
  must("#call-link", HTMLAnchorElement).textContent = `Call ${BRAND.phoneDisplay}`;
  must("#quote-link", HTMLAnchorElement).href = BRAND.quoteUrl;
}

function readBest(): { score: number; houses: number } {
  return {
    score: Number(localStorage.getItem(STORAGE.bestScore) ?? 0),
    houses: Number(localStorage.getItem(STORAGE.bestHouses) ?? 0),
  };
}

function saveBest(stats: RunStats): void {
  const best = readBest();
  if (stats.score > best.score) localStorage.setItem(STORAGE.bestScore, String(stats.score));
  if (stats.housesLit > best.houses) localStorage.setItem(STORAGE.bestHouses, String(stats.housesLit));
  refreshBest();
}

function refreshBest(): void {
  const best = readBest();
  must("#start-best", HTMLElement).textContent =
    best.houses > 0
      ? `Personal best: ${best.houses} houses · ${best.score} points.`
      : "Personal best: no houses yet.";
}

async function renderBoard(): Promise<void> {
  paintBoard(await fetchBoard());
}

function paintBoard(data: Awaited<ReturnType<typeof fetchBoard>>): void {
  must("#board-week", HTMLElement).textContent = `Week ${data.weekId}`;
  const list = must("#board-list", HTMLElement);
  list.replaceChildren();
  if (!data.scores.length) {
    const empty = document.createElement("li");
    empty.textContent = "No runs this week. Be the first roof.";
    list.append(empty);
  } else {
    data.scores.forEach((row, i) => {
      const li = document.createElement("li");
      li.innerHTML = `<span class="rank">${i + 1}</span><span>${escapeHtml(row.name)}</span><span>${row.housesLit} houses · ${row.score}</span>`;
      list.append(li);
    });
  }
  must("#board-stub", HTMLElement).textContent = data.stub
    ? data.source === "local"
      ? "This board is on your phone until D1 or KV is bound. Setup is in the README."
      : "API is in stub mode. Bind D1 or KV to keep a real weekly board."
    : "Resets Monday, America/New_York.";
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[ch] ?? ch));
}

function must<T extends Element>(sel: string, ctor: new (...args: never[]) => T): T {
  const el = document.querySelector(sel);
  if (!(el instanceof ctor)) throw new Error(`Missing ${sel}`);
  return el;
}
